import React, { Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  type NavigateFunction,
  Outlet,
  type Params,
  RouterProvider,
  useNavigate,
  useParams,
} from "react-router-dom";
import { HoxRoot } from "hox";
import { HeroUIProvider } from "@heroui/system";
import NiceModal from "@ebay/nice-modal-react";
import { ToastProvider } from "@heroui/react";
import { useTitle } from "ahooks";
import { useTranslation } from "react-i18next";
import { LayoutLoading } from "@/comps/LayoutLoading";
import { RouterLayout } from "@/common/layout/RouterLayout";
import { initFontListener } from "@/utils/fontManager";
import { useRegistrationData } from "@/store/useRegistrationData";
import bgImg from "@/assets/background.png";
import "@/utils/spinner";

const Home = React.lazy(() => import("./Home"));
const Detail = React.lazy(() => import("./Detail"));
const Registration = React.lazy(() => import("./Registration"));
const NotFound = React.lazy(() => import("./NotFound"));

export const globalRoute: {
  params?: Params<string>;
  navigate?: NavigateFunction;
} = {};
export const GlobalRouteProvider = () => {
  globalRoute.params = useParams();
  globalRoute.navigate = useNavigate();
  return null;
};

const onLayoutInitial = async () => {
  // Perform any initial data fetching or setup here
  if (globalRoute.params?.id) {
    try {
      await useRegistrationData.getState().refresh(globalRoute.params.id);
    } catch (err) {
      globalRoute.navigate?.("/404", { replace: true });
    }
  } else {
    globalRoute.navigate?.("/404", { replace: true });
  }
};

initFontListener();

const router = createBrowserRouter([
  {
    element: (
      <HoxRoot>
        <HeroUIProvider>
          <NiceModal.Provider>
            <ToastProvider
              placement="top-center"
              toastProps={{
                classNames: {
                  content: "gap-x-3",
                  base: "!w-fit m-auto rounded-2xl",
                  title:
                    "font-normal text-color-text text-sm whitespace-pre-wrap overflow-auto me-0",
                },
              }}
              toastOffset={45}
              maxVisibleToasts={1}
            />
            <GlobalRouteProvider />
            <Outlet />
          </NiceModal.Provider>
        </HeroUIProvider>
      </HoxRoot>
    ),
    children: [
      {
        path: "/:id",
        element: <RouterLayout onInitial={onLayoutInitial} />,
        children: [
          {
            path: "",
            element: <Home />,
          },
          {
            path: "registration",
            element: <Registration />,
          },
          {
            path: "detail",
            element: <Detail />,
          },
        ],
      },
      {
        path: "/404",
        element: <NotFound />,
      },
      {
        // 全局未匹配路径（如 /abc、/:id/xxx）→ 重定向到404
        path: "*",
        element: <Navigate to="/404" replace={true} />,
      },
    ],
  },
]);

function App() {
  const { t } = useTranslation();
  useTitle(t("app_name"));
  return (
    <div
      className="h-full overflow-y-auto bg-[#fafafa] bg-cover bg-center"
      style={{
        backgroundImage: `url(${bgImg})`,
      }}
    >
      <Suspense fallback={<LayoutLoading />}>
        <RouterProvider router={router} />
      </Suspense>
    </div>
  );
}

export default App;
